import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminOverview } from "../hooks/useApi";

interface StoreOverview {
  storeId: string;
  storeName: string;
  storeCode: string;
  totalItems: number;
  counted: number;
  percentCounted: number;
  varianceTotal: number;
  lastUpdatedAt: string | null;
}

const ACTIVE_WINDOW_MS = 5 * 60 * 1000;

function formatAgo(ms: number) {
  const sec = Math.max(0, Math.floor(ms / 1000));
  if (sec < 10) return "just now";
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
}

function getStoreActivity(lastUpdatedAt: string | null) {
  if (!lastUpdatedAt) {
    return { label: "No activity today", tone: "muted" as const };
  }
  const ago = Date.now() - new Date(lastUpdatedAt).getTime();
  if (ago < ACTIVE_WINDOW_MS) {
    return { label: "Counting now", tone: "success" as const };
  }
  return { label: `Last update ${formatAgo(ago)}`, tone: "muted" as const };
}

function getStoreStatus(percentCounted: number, varianceTotal: number) {
  if (percentCounted === 100 && varianceTotal >= 0) {
    return { label: "Healthy", tone: "success" as const };
  }
  if (percentCounted >= 60) {
    return { label: "In progress", tone: "warning" as const };
  }
  return { label: "Needs action", tone: "danger" as const };
}

function clampPercent(value: number) {
  return Math.max(0, Math.min(100, Math.round(value)));
}

export function AdminOverview() {
  const {
    data: overview,
    isLoading,
    isFetching,
    dataUpdatedAt,
    refetch,
  } = useAdminOverview();
  const navigate = useNavigate();
  const stores = (overview as StoreOverview[]) || [];
  const [, tick] = useState(0);

  // Re-render every 15s so relative timestamps stay fresh between polls.
  useEffect(() => {
    const id = window.setInterval(() => tick((n) => n + 1), 15_000);
    return () => window.clearInterval(id);
  }, []);

  const totalItems = stores.reduce((sum, store) => sum + store.totalItems, 0);
  const countedItems = stores.reduce((sum, store) => sum + store.counted, 0);
  const completionRate = totalItems
    ? Math.round((countedItems / totalItems) * 100)
    : 0;
  const atRiskStores = stores.filter(
    (store) => store.percentCounted < 100 || store.varianceTotal < 0
  ).length;
  const combinedVariance = stores.reduce(
    (sum, store) => sum + store.varianceTotal,
    0
  );
  const activeStores = stores.filter((store) => {
    if (!store.lastUpdatedAt) return false;
    return Date.now() - new Date(store.lastUpdatedAt).getTime() < ACTIVE_WINDOW_MS;
  }).length;

  if (isLoading) {
    return (
      <div className="page-shell">
        <p className="state-note">Loading inventory overview…</p>
      </div>
    );
  }

  return (
    <div className="page-shell">
      <header className="page-header">
        <div>
          <h1>Inventory overview</h1>
          <p>Today&apos;s stock count progress across all stores.</p>
        </div>
        <div className="overview-refresh">
          <p className="overview-refresh-meta">
            Updated {formatAgo(Date.now() - dataUpdatedAt)}
            {isFetching ? " · refreshing…" : ""}
          </p>
          <button
            type="button"
            className="overview-refresh-button"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            Refresh now
          </button>
        </div>
      </header>

      <section className="stats-grid">
        <article className="stat-card">
          <p className="stat-label">Stores</p>
          <p className="stat-value">{stores.length}</p>
        </article>
        <article className="stat-card">
          <p className="stat-label">Count completion</p>
          <p className="stat-value">{completionRate}%</p>
        </article>
        <article className="stat-card">
          <p className="stat-label">Counting now</p>
          <p className="stat-value">{activeStores}</p>
        </article>
        <article className="stat-card">
          <p className="stat-label">Needs attention</p>
          <p className="stat-value">{atRiskStores}</p>
        </article>
        <article className="stat-card">
          <p className="stat-label">Combined variance</p>
          <p className="stat-value">{combinedVariance.toFixed(2)}</p>
        </article>
      </section>

      {stores.length === 0 ? (
        <p className="state-note">No store data available yet.</p>
      ) : (
        <>
          <div className="inventory-table-wrap desktop-only">
            <table className="inventory-table">
              <thead>
                <tr>
                  <th>Store</th>
                  <th>Stock progress</th>
                  <th>Activity</th>
                  <th>Status</th>
                  <th className="table-number">Variance total</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {stores.map((store) => {
                  const status = getStoreStatus(
                    store.percentCounted,
                    store.varianceTotal
                  );
                  const activity = getStoreActivity(store.lastUpdatedAt);
                  const progress = clampPercent(store.percentCounted);
                  return (
                    <tr key={store.storeId}>
                      <td>
                        <p className="table-item-title">{store.storeName}</p>
                        <p className="table-item-subtitle">{store.storeCode}</p>
                      </td>
                      <td>
                        <div className="progress-shell">
                          <span
                            className={`progress-fill ${
                              progress === 100
                                ? "is-complete"
                                : progress >= 60
                                  ? "is-mid"
                                  : "is-low"
                            }`}
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                        <div className="progress-meta">
                          <span>
                            {store.counted}/{store.totalItems} counted
                          </span>
                          <span>{progress}%</span>
                        </div>
                      </td>
                      <td>
                        <span className={`badge badge-${activity.tone}`}>
                          {activity.label}
                        </span>
                      </td>
                      <td>
                        <span className={`badge badge-${status.tone}`}>
                          {status.label}
                        </span>
                      </td>
                      <td
                        className={`table-number ${
                          store.varianceTotal < 0
                            ? "metric-negative"
                            : store.varianceTotal > 0
                              ? "metric-positive"
                              : "metric-neutral"
                        }`}
                      >
                        {store.varianceTotal.toFixed(2)}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="table-action"
                          onClick={() => navigate(`/admin/store/${store.storeId}`)}
                        >
                          Open day
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="store-card-list mobile-only">
            {stores.map((store) => {
              const status = getStoreStatus(
                store.percentCounted,
                store.varianceTotal
              );
              const activity = getStoreActivity(store.lastUpdatedAt);
              const progress = clampPercent(store.percentCounted);
              return (
                <article key={store.storeId} className="store-card">
                  <div className="store-card-top">
                    <div>
                      <p className="table-item-title">{store.storeName}</p>
                      <p className="table-item-subtitle">{store.storeCode}</p>
                    </div>
                    <span className={`badge badge-${status.tone}`}>
                      {status.label}
                    </span>
                  </div>

                  <div className="store-activity-row">
                    <span className={`badge badge-${activity.tone}`}>
                      {activity.label}
                    </span>
                  </div>

                  <div className="store-progress-block">
                    <div className="progress-shell">
                      <span
                        className={`progress-fill ${
                          progress === 100
                            ? "is-complete"
                            : progress >= 60
                              ? "is-mid"
                              : "is-low"
                        }`}
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <div className="progress-meta">
                      <span>
                        {store.counted}/{store.totalItems} counted
                      </span>
                      <span>{progress}%</span>
                    </div>
                  </div>

                  <div className="store-metric-row">
                    <span>Variance</span>
                    <strong
                      className={
                        store.varianceTotal < 0
                          ? "metric-negative"
                          : store.varianceTotal > 0
                            ? "metric-positive"
                            : "metric-neutral"
                      }
                    >
                      {store.varianceTotal.toFixed(2)}
                    </strong>
                  </div>

                  <button
                    type="button"
                    className="table-action"
                    onClick={() => navigate(`/admin/store/${store.storeId}`)}
                  >
                    Open store day
                  </button>
                </article>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
